import { CognitoUser } from 'amazon-cognito-identity-js';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import UsersClient from 'src/clients/UsersClient';
import { AppViewMode } from 'src/constants/uiConsts';
import { PortalConfigContext } from 'src/context';
import { useCustomerSubscription } from 'src/hooks/useCustomerSubscription';
import {
  useLazyGetCompaniesQuery,
  useLazyGetOtherUserDataQuery,
  useLazyGetUserFileChannelsQuery,
  useLazyGetUserNotificationsQuery,
  useLazyGetUserSettingsQuery,
  useLazyGetUsersQuery,
} from 'src/services/api';
import { useLazyGetOnboardingTaskStatusQuery } from 'src/services/api/improvedOnboardingApi';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';

interface ErrorWithDetails {
  code?: string;
  message?: string;
  name?: string;
}

const isErrorWithDetails = (err: unknown): err is ErrorWithDetails => {
  return (
    typeof err === 'object' &&
    err !== null &&
    ('code' in err || 'message' in err || 'name' in err)
  );
};

const AUTH_ERROR_MESSAGES = [
  'not authenticated',
  'no current user',
  'user is not authenticated',
];

const containsAuthErrorMessage = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return AUTH_ERROR_MESSAGES.some((authError) =>
    lowerMessage.includes(authError),
  );
};

/**
 * This hook is used to load all the user data.
 * On mount, it gets the authenticated user from Cognito
 * and pass it to all the queries that need it.
 * @param userLoaded - when user data is not loaded, fire the queries
 */
export const useGetUserDataQueries = (
  userLoaded: boolean,
  flexibleAuth?: boolean,
) => {
  const [authUser, setAuthUser] = useState<CognitoUser>();
  const portalConfig = useContext(PortalConfigContext);
  const dispatch = useDispatch();

  const authUserId = useMemo(() => authUser?.getUsername() || '', [authUser]);
  const appViewMode = portalConfig.viewMode;
  const { allSubscriptions } = useCustomerSubscription();

  const [loadUsers] = useLazyGetUsersQuery();
  const [loadCompanies] = useLazyGetCompaniesQuery();
  const [loadUserFileChannels] = useLazyGetUserFileChannelsQuery();
  const [loadUserNotifications] = useLazyGetUserNotificationsQuery();
  const [loadUserOtherData] = useLazyGetOtherUserDataQuery();
  const [loadUserSettings] = useLazyGetUserSettingsQuery();
  const [loadOnboardingTaskStatus] = useLazyGetOnboardingTaskStatusQuery();

  /**
   * Determines if an error indicates a true authentication failure
   * vs a transient network/server error.
   *
   * Authentication failures include:
   * - "not authenticated" or "The user is not authenticated" messages from Amplify
   * - NotAuthorizedException from Cognito
   * - UserNotFoundException from Cognito
   *
   * Transient errors (should NOT force logout):
   * - Network errors (connectivity issues, tab suspension/wake)
   * - Server errors (5xx responses)
   * - Timeout errors
   */
  const isAuthenticationError = (err: unknown): boolean => {
    if (!err) return false;

    // Handle string errors thrown by Amplify (e.g., "The user is not authenticated")
    if (typeof err === 'string') {
      return containsAuthErrorMessage(err);
    }

    if (!isErrorWithDetails(err)) return false;

    const errorMessage = err.message?.toLowerCase() ?? '';
    const errorCode = err.code ?? '';
    const errorName = err.name ?? '';

    // Cognito/Amplify authentication error codes
    const authErrorCodes = [
      'NotAuthorizedException',
      'UserNotFoundException',
      'UserNotConfirmedException',
      'PasswordResetRequiredException',
    ];

    // Check for Cognito error codes
    if (authErrorCodes.includes(errorCode)) {
      return true;
    }

    // Check for Amplify "not authenticated" message
    if (containsAuthErrorMessage(errorMessage)) {
      return true;
    }

    // Network errors should NOT trigger logout
    if (
      errorName === 'NetworkError' ||
      errorCode === 'NetworkError' ||
      errorMessage.includes('network') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('aborted')
    ) {
      return false;
    }

    // Default to false - don't force logout for unknown errors
    // This is safer than potentially logging out users due to transient issues
    return false;
  };

  /**
   * This function initializes the authenticated user state
   */
  const initializeAuthenticatedUser = async () => {
    try {
      const authenticatedUser = await UsersClient.getAuthUser({
        portalId: portalConfig.portalHeader,
        portalName: portalConfig.subdomain,
      });
      setAuthUser(authenticatedUser);
    } catch (err) {
      if (!flexibleAuth) {
        // Only force signout for true authentication failures
        // Network errors or transient issues should not log the user out
        if (isAuthenticationError(err)) {
          console.error('User not authenticated, redirecting to login', err);
          // when auth user cannot be found there is a corrupted state
          // in the sessions and we need to sign out all sessions
          window.location.href = '/auth/signout-all';
        } else {
          // Log the error but don't force logout for transient errors
          // This handles cases like tab suspension, network reconnection, etc.
          console.warn(
            'Failed to get authenticated user due to transient error, not forcing logout',
            err,
          );
        }
      }
    }
  };

  useEffect(() => {
    initializeAuthenticatedUser();
  }, []);

  useEffect(() => {
    // when user is not loaded and authUser is set, load the user data
    if (authUser && !userLoaded) {
      // Fire all requests in parallel immediately. If any fail due to invalid
      // activeCompanyId, we'll detect it from settings (which blocks the sidebar)
      // and retry everything with the corrected company ID.
      const fetchAllData = async () => {
        const settingsResult = await loadUserSettings(authUserId);

        if (settingsResult.error) {
          // Settings failed, likely due to invalid activeCompanyId.
          // Clear it, reload companies, and retry everything.
          localStorage.removeItem('activeCompanyId');
          dispatch(setActiveCompanyId(''));

          const companies = await loadCompanies(authUserId);
          const activeCompanyId = companies.data?.companies.at(0)?.id;

          if (activeCompanyId) {
            localStorage.setItem('activeCompanyId', activeCompanyId);
            dispatch(setActiveCompanyId(activeCompanyId));
          }

          // Retry all requests with corrected activeCompanyId
          await Promise.all([
            loadUserSettings(authUserId),
            loadUsers({
              userId: authUserId,
              appViewMode,
            }),
            loadCompanies(authUserId),
            loadUserFileChannels(authUserId),
            loadUserNotifications(authUserId),
            loadUserOtherData({
              cognitoUserId: authUserId,
            }),
          ]);
        } else {
          // Settings succeeded, fire the rest in parallel
          await Promise.all([
            loadUsers({
              userId: authUserId,
              appViewMode,
            }),
            loadCompanies(authUserId),
            loadUserFileChannels(authUserId),
            loadUserNotifications(authUserId),
            loadUserOtherData({
              cognitoUserId: authUserId,
            }),
          ]);
        }
      };

      fetchAllData().catch((err) => {
        console.error('! error loading user data', err);
      });
    }
  }, [
    authUser,
    userLoaded,
    appViewMode,
    authUserId,
    dispatch,
    loadUsers,
    loadCompanies,
    loadUserFileChannels,
    loadUserNotifications,
    loadUserOtherData,
    loadUserSettings,
  ]);

  useEffect(() => {
    if (appViewMode === AppViewMode.INTERNAL) {
      loadOnboardingTaskStatus({ subscriptions: allSubscriptions });
    }
  }, [allSubscriptions, appViewMode, loadOnboardingTaskStatus]);
};
